import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DtcAPI, TechDataAPI, extractError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DtcCodeChip } from "./DtcCodeChip";
import { DtcIngestDialog } from "./DtcIngestDialog";

function ServiceBriefCard({ vehicleId }) {
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!vehicleId) { setLoading(false); return; }
        TechDataAPI.lookup(vehicleId)
            .then(setBundle)
            .catch(() => setBundle(null))
            .finally(() => setLoading(false));
    }, [vehicleId]);

    if (loading) {
        return <div data-testid="service-brief-loading" className="rounded-xl border p-3 text-sm opacity-60">Loading service brief…</div>;
    }
    if (!bundle) {
        return <div data-testid="service-brief-empty" className="rounded-xl border p-3 text-sm opacity-60">No tech-data brief available for this vehicle.</div>;
    }

    return (
        <div data-testid="service-brief-card" className="rounded-xl border bg-card p-4 space-y-3">
            <div className="flex items-baseline justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wider opacity-60">Service brief</div>
                    <div data-testid="service-brief-vehicle" className="text-sm font-medium">
                        {bundle.vehicle?.make} {bundle.vehicle?.model} {bundle.vehicle?.year || ""}
                    </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300">stub data</span>
            </div>
            {bundle.maintenance_due_at_next_visit && (
                <div data-testid="service-brief-maintenance-row" className="text-sm">
                    <span className="text-xs uppercase tracking-wider opacity-60 mr-2">Next service</span>
                    <span className="font-medium">{bundle.maintenance_due_at_next_visit.title}</span>
                </div>
            )}
            {Array.isArray(bundle.torque_specs) && bundle.torque_specs.length > 0 && (
                <div data-testid="service-brief-torque-row" className="text-sm">
                    <span className="text-xs uppercase tracking-wider opacity-60 mr-2">Torque</span>
                    <span className="font-medium">{bundle.torque_specs[0].title}</span>
                </div>
            )}
            {Array.isArray(bundle.recent_bulletins) && bundle.recent_bulletins.length > 0 && (
                <div data-testid="service-brief-bulletin-row" className="text-sm">
                    <span className="text-xs uppercase tracking-wider opacity-60 mr-2">Bulletin</span>
                    <span className="font-medium">{bundle.recent_bulletins[0].title}</span>
                </div>
            )}
            {Array.isArray(bundle.recalls) && bundle.recalls.length > 0 && (
                <div data-testid="service-brief-recall-row" className="text-sm text-rose-700 dark:text-rose-300">
                    <span className="text-xs uppercase tracking-wider opacity-60 mr-2">Recall</span>
                    <span className="font-medium">{bundle.recalls[0].title}</span>
                </div>
            )}
        </div>
    );
}

function IncidentCard({ incident, jobId, onCleared }) {
    const { permissions } = useAuth();
    const canClear = (permissions || []).includes("jobcard:dtc_clear");
    const [clearing, setClearing] = useState(false);
    const [reason, setReason] = useState("");
    const [showClearInput, setShowClearInput] = useState(false);

    const handleClear = async () => {
        setClearing(true);
        try {
            await DtcAPI.clearIncident(jobId, incident.id, { reason });
            toast.success("DTC cleared");
            onCleared?.();
        } catch (e) { toast.error(extractError(e)); }
        finally { setClearing(false); setShowClearInput(false); setReason(""); }
    };

    const decoded = incident.decoded || {};
    const isCleared = !!incident.cleared_at;

    return (
        <div data-testid={`dtc-incident-${incident.id}`}
                className={`rounded-xl border p-3 space-y-2 ${isCleared ? "opacity-60" : ""} bg-card`}>
            <div className="flex items-center justify-between gap-3">
                <DtcCodeChip code={incident.code}
                                  severity={decoded.severity}
                                  description={decoded.description}
                                  data-testid={`dtc-incident-${incident.id}-chip`} />
                <div className="text-[10px] uppercase tracking-widest opacity-50">
                    {new Date(incident.read_at).toLocaleString()}
                </div>
            </div>
            <div data-testid="dtc-incident-description" className="text-sm">
                {decoded.description || "Unknown DTC"}
            </div>
            {incident.mileage_at_read != null && (
                <div className="text-xs opacity-70">Mileage at read: {incident.mileage_at_read.toLocaleString()} km</div>
            )}
            {Array.isArray(decoded.affected_systems) && decoded.affected_systems.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {decoded.affected_systems.map((s) => (
                        <span key={s} className="text-[10px] uppercase tracking-widest rounded-full bg-muted px-2 py-0.5">{s}</span>
                    ))}
                </div>
            )}
            {Array.isArray(incident.repair_data_entry_ids) && incident.repair_data_entry_ids.length > 0 && (
                <div data-testid={`dtc-incident-${incident.id}-repair-links`}
                        className="text-xs">
                    <span className="opacity-60">Suggested procedure: </span>
                    {incident.repair_data_entry_ids.map((rid) => (
                        <a key={rid} href={`/tech-data/${rid}`} className="underline mr-2"
                              data-testid={`dtc-incident-repair-link-${rid}`}>
                            view
                        </a>
                    ))}
                </div>
            )}
            {isCleared ? (
                <div className="text-xs">
                    <span className="rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-0.5">cleared</span>
                    {incident.cleared_reason && (
                        <span data-testid={`dtc-incident-${incident.id}-cleared-reason`} className="ml-2 opacity-70">{incident.cleared_reason}</span>
                    )}
                </div>
            ) : canClear && (
                showClearInput ? (
                    <div className="flex gap-2">
                        <Input value={reason} onChange={(e) => setReason(e.target.value)}
                                   data-testid={`dtc-incident-${incident.id}-clear-reason`}
                                   placeholder="Repair note (e.g. coil pack replaced)" />
                        <Button onClick={handleClear} disabled={clearing}
                                  data-testid={`dtc-incident-${incident.id}-clear-confirm`}>
                            {clearing ? "…" : "Clear"}
                        </Button>
                    </div>
                ) : (
                    <Button variant="outline" size="sm"
                              data-testid={`dtc-incident-${incident.id}-clear-btn`}
                              onClick={() => setShowClearInput(true)}>
                        Mark as cleared
                    </Button>
                )
            )}
        </div>
    );
}

export function DiagnosticsTab({ job }) {
    const { permissions } = useAuth();
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const jobId = job?.id;

    const load = useCallback(() => {
        if (!jobId) return;
        setLoading(true);
        DtcAPI.listForJob(jobId)
            .then((d) => setIncidents(d.items || []))
            .catch(() => setIncidents([]))
            .finally(() => setLoading(false));
    }, [jobId]);

    useEffect(() => { load(); }, [load]);

    const canIngest = (permissions || []).includes("jobcard:dtc_ingest");

    return (
        <div data-testid="dtc-tab-panel" className="space-y-4">
            <ServiceBriefCard vehicleId={job?.vehicle_id} />

            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">Diagnostic trouble codes</h3>
                {canIngest && <DtcIngestDialog jobId={jobId} onIngested={load} />}
            </div>

            {loading ? (
                <div className="text-sm opacity-60">Loading…</div>
            ) : incidents.length === 0 ? (
                <div data-testid="dtc-empty" className="rounded-xl border border-dashed p-6 text-sm text-center opacity-60">
                    No DTCs scanned for this job yet.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dtc-incident-list">
                    {incidents.map((inc) => (
                        <IncidentCard key={inc.id} incident={inc} jobId={jobId} onCleared={load} />
                    ))}
                </div>
            )}
        </div>
    );
}
